'use client'

import React, { FC, ReactElement } from 'react'
import classNames from 'classnames'
import CollectionIcon from '@/components/collection-icon'
import Link from '@/components/link'
import Skeleton from '@/components/skeleton'
import formatNumber from '@/utils/format-number'
import { NETWORK_FUNGIBLE_PROTOCOL } from '@/utils/constants'
import isRune from '@/utils/is-rune'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

const WalletCoinCard: FC<{
	ticker: string
	balanceCoin: string
	balanceUsd: string
	tickerIcon?: any
	onClick?: () => void
	isLoading?: boolean
	hideUsd?: boolean
	collection?: any
	type?: any
	description?: any
	params?: any
}> = (props): ReactElement => {
	const {
		type,
		collection,
		ticker,
		tickerIcon,
		balanceCoin,
		balanceUsd,
		isLoading,
		description,
		params
	} = props

	const slug = React.useMemo(
		() =>
			collection?.slug
				? collection?.slug
				: type
				? `${type.replace('-', '')}-${ticker}`
				: `${NETWORK_FUNGIBLE_PROTOCOL.replace('-', '')}-${ticker.replace('.', 'dot')}`,
		[ticker, type, collection]
	)

	const renderTicker = () => {
		if (ticker === 'BTC') {
			return <span className={classNames('text-xs', 'bold')}>${ticker}</span>
		} else {
			return (
				<Link href={`/collection/${slug}`} onClick={(evt) => evt.stopPropagation()}>
					<span className={classNames('text-xs bold hover:underline flex items-center')}>
						{ticker ? (
							<>
								{window.innerWidth <= 400 && ticker.length > 12
									? `${ticker.slice(0, 12).toUpperCase()}...`
									: ticker.length > 12
									? `${ticker.slice(0, 16).toUpperCase()}...`
									: ticker.toUpperCase()}
							</>
						) : (
							''
						)}
						<span className="text-xs font-normal text-muted-foreground ml-2">{type}</span>
					</span>
				</Link>
			)
		}
	}

	return (
		<div
			className="w-full p-4 bg-zinc-900 rounded-lg flex items-center justify-center gap-2.5 transition-all duration-200 ease-in-out cursor-pointer hover:-translate-y-0.5"
			onClick={props?.onClick}
			role="button"
			tabIndex={0}
			onKeyDown={onKeyboardClick(props?.onClick)}
		>
			<div className="flex items-center justify-center">
				{tickerIcon ? (
					tickerIcon
				) : (
					<CollectionIcon
						collection={collection}
						className="size-9 min-h-9 min-w-9 rounded-full overflow-hidden"
						imageClassName="size-9 min-h-9 min-w-9 rounded-full overflow-hidden"
					/>
				)}
			</div>
			<div className="w-full flex flex-col justify-between">
				<div className="flex justify-between items-center">
					{renderTicker()}
					{isLoading ? (
						<Skeleton className="h-4.5 w-25" />
					) : (
						<span className={classNames('text-xs', 'bold')}>
							{formatNumber(balanceCoin)}
							{isRune(slug) && ` ${params?.symbol}`}
						</span>
					)}
				</div>
				{!props.hideUsd && (
					<div className="text-muted-foreground flex justify-between items-center">
						<span className={classNames('text-xs', 'medium')}>{description || 'Balance'}</span>
						{isLoading ? (
							<Skeleton className="h-4.5 w-25" />
						) : (
							<span className={classNames('text-xs', 'medium')}>{`~$${balanceUsd}`}</span>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

export default WalletCoinCard
